// Pure helpers for the insurance dash. No React and no Graph calls: the page
// loads Household_Policies and hands the rows in. Every policy fact lives in
// SharePoint; nothing here knows a policy number, a limit or a carrier.

const day = v => (v || '').slice(0, 10);   // string-safe — SharePoint dateOnly reads back as site-local midnight

// Multi-line text → trimmed, non-empty lines.
export const lines = v => String(v || '').split(/\r?\n/).map(s => s.trim()).filter(Boolean);

// Days from `today` (a local Date) to a bare 'YYYY-MM-DD'. Both ends become
// calendar days in UTC, so the hour, the timezone and DST never shift the count.
export function daysUntil(yyyymmdd, today = new Date()) {
  if (!yyyymmdd) return null;
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  const t = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((Date.UTC(y, m - 1, d) - t) / 86400000);
}
const localDate = yyyymmdd => { const [y, m, d] = yyyymmdd.split('-').map(Number); return new Date(y, m - 1, d); };

// Renewal color: green, amber inside 45 days, red inside 15.
export const RENEW_AMBER = 45;
export const RENEW_RED   = 15;
export function renewalTone(days) {
  if (days === null) return 'none';
  if (days <= RENEW_RED) return 'red';
  if (days <= RENEW_AMBER) return 'amber';
  return 'green';
}

// "10/30" and "10/30/26" — no leading zeros.
export const fmtMD  = yyyymmdd => (yyyymmdd ? `${Number(yyyymmdd.slice(5, 7))}/${Number(yyyymmdd.slice(8, 10))}` : '');
export const fmtMDY = yyyymmdd => (yyyymmdd ? `${fmtMD(yyyymmdd)}/${yyyymmdd.slice(2, 4)}` : '');

// A Household_Policies row → what the page draws.
export function toPolicy(row, today = new Date()) {
  const eff = day(row.EffectiveDate), exp = day(row.ExpirationDate);
  const toRenew = daysUntil(exp, today);
  const toStart = daysUntil(eff, today);
  const term = eff && exp ? daysUntil(exp, localDate(eff)) : null;
  return {
    id: row.id,
    title: row.Title || row.LOB || 'Policy',
    lob: row.LOB || 'Other',
    status: row.Status || '',
    number: row.PolicyNumber || '',
    carrier: row.Carrier || '',
    eff, exp,
    premium: row.Premium || '',
    limits: lines(row.KeyLimits),
    insuredAddress: row.InsuredAddress || '',
    insureds: row.Insureds || '',
    decPath: (row.DecPagePath || '').trim(),
    // Full package first, then the forms (or an earlier version).
    fullPaths: lines(row.FullPolicyPaths),
    idCards: lines(row.IDCardImagePaths),
    agentName: row.AgentName || '',
    agentPhone: row.AgentPhone || '',
    claimsPhone: row.ClaimsPhone || '',
    openItems: lines(row.OpenItems),
    sortOrder: Number.isFinite(row.SortOrder) ? row.SortOrder : 999,
    toRenew,
    tone: renewalTone(toRenew),
    notStarted: toStart !== null && toStart > 0,
    // Share of the term still ahead, for the ring (0..1).
    termLeft: term ? Math.max(0, Math.min(1, toRenew / term)) : null,
  };
}

// Superseded and cancelled policies are left out; the rest go by SortOrder.
export function policies(rows, today = new Date()) {
  return (rows || [])
    .filter(r => r.Status !== 'Superseded' && r.Status !== 'Cancelled')
    .map(r => toPolicy(r, today))
    .sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));
}

// Every ID card across the policies, in policy order, each with its policy.
export const idCards = list => list.flatMap(p => p.idCards.map(path => ({ path, policy: p })));

// The second full-policy file's link name: "Forms" for the policy forms,
// otherwise it is an older copy of the package.
export const extraDocLabel = path => (/PolicyForms/i.test(path || '') ? 'Forms' : 'Earlier version');

// "(713) 830-3288" → "tel:7138303288"
export const telHref = phone => `tel:${String(phone || '').replace(/[^\d+]/g, '')}`;

// IDCard_2026_Odyssey_897104331.png → "2026 Odyssey"
export function cardLabel(path) {
  const name = (path.split('/').pop() || '').replace(/\.[a-z]+$/i, '');
  const m = name.match(/^IDCard_(.+?)_\d{6,}$/);
  return (m ? m[1] : name).replace(/_/g, ' ');
}
